import React, { useState, useEffect } from "react";
import icons from "./data/icons";
import { useLocation } from "react-router-dom";

import { getHome } from "./api/homeService";
import { getHeaderTree } from "./api/headerService";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [activeMenuChild, setActiveMenuChild] = useState("");
  const [dateTime, setDateTime] = useState(new Date());
  const [isOpen, setIsOpen] = useState(false);
  const [isProgrammaticScroll, setIsProgrammaticScroll] = useState(false);
  const [activeProjectFilter, setActiveProjectFilter] =
    useState(null);

  const [projectsClicked, setProjectsClicked] =
    useState(false);

  const [home, setHome] = useState(null);
  const [navItems, setNavItems] = useState([]);
  const [mobileDropdownOpen, setMobileDropdownOpen] =
    useState(null);

  const [showBackToTop, setShowBackToTop] =
    useState(false);

  const location = useLocation();

  const getItemKey = (item) =>
    item?.target || item?.id || "";

  const getChildKey = (child) => {
    if (child?.type === "projects" && child?.filter) {
      return `project:${child.filter}`;
    }

    return getItemKey(child);
  };

  const getCurrentChildKey = (child) => {
    if (child?.type === "projects" && child?.filter) {
      return `project:${child.filter}`;
    }

    return getItemKey(child);
  };

  const isChildActive = (child) => {
    const childKey = getChildKey(child);

    if (
      child.type === "projects" &&
      child.filter
    ) {
      return activeProjectFilter === child.filter;
    }

    return (
      activeMenuChild === childKey ||
      activeSection === getItemKey(child)
    );
  };

  useEffect(() => {
    const loadHome = async () => {
      try {
        const response = await getHome();

        const raw =
          response?.data?.data ??
          response?.data ??
          null;

        const homeData = Array.isArray(raw)
          ? raw[0]
          : raw;

        setHome(homeData || null);
      } catch (error) {
        console.error(
          "Failed to load home data:",
          error
        );
      }
    };

    loadHome();
  }, []);

  useEffect(() => {
    const loadHeader = async () => {
      try {
        const response = await getHeaderTree();

        const data =
          response?.data?.data ??
          response?.data ??
          [];

        setNavItems(
          Array.isArray(data) ? data : []
        );
      } catch (error) {
        console.error(
          "Failed to load header:",
          error
        );
      }
    };

    loadHeader();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", onScroll);
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const resolveIcon = (iconName) => {
    if (
      !iconName ||
      typeof iconName !== "string"
    ) {
      return null;
    }

    const cleanName = iconName.trim();

    if (!cleanName) {
      return null;
    }

    return icons[cleanName] || null;
  };

  const isInsideProjects = () => {
    const projectsElement =
      document.getElementById("projects");

    if (!projectsElement) {
      return false;
    }

    const top = projectsElement.offsetTop;
    const bottom =
      top + projectsElement.offsetHeight;

    const scrollPosition =
      window.scrollY + 200;

    return (
      scrollPosition >= top &&
      scrollPosition < bottom
    );
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isProgrammaticScroll) {
        return;
      }

      if (
        projectsClicked &&
        isInsideProjects()
      ) {
        setActiveSection("projects");
        return;
      }

      if (
        isInsideProjects() &&
        activeProjectFilter !== "project"
      ) {
        setActiveSection("");
        return;
      }

      const targets = new Set();

      const collectTargets = (items) => {
        items.forEach((item) => {
          if (item.show === false) {
            return;
          }

          const target =
            item.target || item.id;

          if (target) {
            targets.add(target);
          }

          if (Array.isArray(item.children)) {
            collectTargets(item.children);
          }
        });
      };

      collectTargets(navItems);

      const scrollPosition =
        window.scrollY + 200;

      const targetArray =
        Array.from(targets).reverse();

      for (const target of targetArray) {
        const element =
          document.getElementById(target);

        if (!element) {
          continue;
        }

        if (
          scrollPosition >=
          element.offsetTop
        ) {
          setActiveSection(target);
          break;
        }
      }
    };

    let timeoutId;

    const debouncedHandleScroll = () => {
      clearTimeout(timeoutId);

      timeoutId = setTimeout(
        handleScroll,
        100
      );
    };

    window.addEventListener(
      "scroll",
      debouncedHandleScroll
    );

    handleScroll();

    return () => {
      window.removeEventListener(
        "scroll",
        debouncedHandleScroll
      );

      clearTimeout(timeoutId);
    };
  }, [
    navItems,
    projectsClicked,
    activeProjectFilter,
    isProgrammaticScroll
  ]);

  useEffect(() => {
    if (
      !location.state?.scrollToProjects
    ) {
      return;
    }

    const filter =
      location.state?.filter;

    if (filter === "project") {
      setActiveSection("projects");
      setProjectsClicked(true);
    } else {
      setActiveSection("");
      setProjectsClicked(false);
    }

    if (filter) {
      setActiveProjectFilter(filter);
      setActiveMenuChild(
        `project:${filter}`
      );
    }
  }, [location.state]);

  const toggleMenu = () => {
    setIsMenuOpen((previous) => !previous);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setMobileDropdownOpen(null);
  };

  useEffect(() => {
    const handlePortfolioFilterChange = (event) => {
      const filter = event.detail?.filter;

      setActiveProjectFilter(filter);
      setActiveMenuChild(`project:${filter}`);

      if (filter === "project") {
        setActiveSection("projects");
        setProjectsClicked(true);
      } else {
        setProjectsClicked(false);
        setActiveSection("");
      }
    };

    window.addEventListener(
      "portfolioFilterChange",
      handlePortfolioFilterChange
    );

    return () => {
      window.removeEventListener(
        "portfolioFilterChange",
        handlePortfolioFilterChange
      );
    };
  }, []);

  useEffect(() => {
    const handleNavbarProjectsClick = (event) => {
      if (
        event.detail?.section ===
        "project"
      ) {
        setActiveProjectFilter("project");
        setActiveMenuChild("project:project");
        setProjectsClicked(true);
        setActiveSection("projects");
      }
    };

    window.addEventListener(
      "navbarProjectsClick",
      handleNavbarProjectsClick
    );

    return () => {
      window.removeEventListener(
        "navbarProjectsClick",
        handleNavbarProjectsClick
      );
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow =
      isMenuOpen ? "hidden" : "unset";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  const handleSmoothScroll = (event, targetId) => {
    event.preventDefault();

    // Immediately make ONLY the clicked section active
    setActiveSection(targetId);
    setActiveMenuChild(targetId);

    // Ignore scroll-based active changes during smooth scrolling
    setIsProgrammaticScroll(true);

    closeMenu();

    const element = document.getElementById(targetId);

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    // Re-enable scroll detection after smooth scrolling finishes
    setTimeout(() => {
      setIsProgrammaticScroll(false);
    }, 800);

    if (targetId === "projects") {
      window.dispatchEvent(
        new CustomEvent("navbarProjectsClick", {
          detail: {
            section: "project",
          },
        })
      );
    } else {
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("leaveProjects")
        );
      }, 300);
    }
  };

  const handlePortfolioNavigation = (
    event,
    button
  ) => {
    const filter = button?.filter;

    if (!filter) {
      return;
    }

    const childKey = `project:${filter}`;

    setActiveMenuChild(childKey);
    setActiveProjectFilter(filter);

    handleSmoothScroll(
      event,
      "projects"
    );

    window.dispatchEvent(
      new CustomEvent(
        "portfolioFilterChange",
        {
          detail: {
            filter,
          },
        }
      )
    );

    window.dispatchEvent(
      new CustomEvent(
        "navbarProjectsClick",
        {
          detail: {
            section: filter,
          },
        }
      )
    );
  };

  const handleNavClick = (
    event,
    item
  ) => {
    const target = getItemKey(item);

    const hasDropdown =
      item.hasDropdown &&
      Array.isArray(item.children) &&
      item.children.length > 0;

    if (hasDropdown) {
      event.preventDefault();

      setActiveSection(target);
      setActiveMenuChild("");

      return;
    }

    if (item.type === "projects") {
      handlePortfolioNavigation(
        event,
        item
      );

      return;
    }

    if (
      item.type === "route" &&
      item.path
    ) {
      event.preventDefault();

      setActiveProjectFilter(null);
      setProjectsClicked(false);
      setActiveSection("");
      setActiveMenuChild("");
      closeMenu();

      window.location.href = item.path;
      return;
    }

    setActiveProjectFilter(null);
    setProjectsClicked(false);
    setActiveMenuChild(target);

    handleSmoothScroll(
      event,
      target
    );
  };

  const handleMobileParentClick = (
    event,
    item
  ) => {
    event.preventDefault();

    const target = getItemKey(item);

    setActiveSection(target);
    setActiveMenuChild("");

    setMobileDropdownOpen((previous) =>
      previous === item.id
        ? null
        : item.id
    );
  };

  const toggleMobileDropdown = (itemId) => {
    setMobileDropdownOpen((previous) =>
      previous === itemId
        ? null
        : itemId
    );
  };

  if (!home) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0C121D]">
        <div className="relative h-14 w-14 animate-spin rounded-full border-[6px] border-slate-700 border-t-indigo-500" />
      </div>
    );
  }

  const visibleItems = navItems.filter(
    (item) =>
      item.show !== false &&
      item.isActive !== false
  );

  const bottomItems = visibleItems
    .filter(
      (item) =>
        item.showInBottom === true &&
        item.parentId == null
    )
    .sort(
      (first, second) =>
        Number(
          first.bottomOrder ??
          first.sortOrder ??
          0
        ) -
        Number(
          second.bottomOrder ??
          second.sortOrder ??
          0
        )
    );

  const featuredBottomItem =
    bottomItems.find(
      (item) =>
        item.bottomFeatured === true
    );

  const normalBottomItems =
    bottomItems.filter(
      (item) =>
        item.id !==
        featuredBottomItem?.id
    );

  const bottomIds = new Set(
    bottomItems.map((item) => item.id)
  );

  const menuItems = visibleItems.filter(
    (item) => !bottomIds.has(item.id)
  );

  const getBottomIcon = (item) => {
    const iconName =
      item.bottomIcon?.trim() ||
      item.icon?.trim() ||
      "";

    return resolveIcon(iconName);
  };

  const renderBottomItem = (item) => {
    const Icon = getBottomIcon(item);
    const target = getItemKey(item);

    const isActive = activeSection === target;

    return (
      <a
        key={item.id}
        href={`#${target}`}
        onClick={(event) =>
          handleNavClick(event, item)
        }
        className="w-full h-full flex flex-col items-center justify-center gap-1 py-1 text-center min-w-0 select-none"
      >
        {Icon && (
          <Icon
            size={20}
            strokeWidth={2}
            color={
              isActive
                ? "#818CF8"
                : item.bottomIconColor ||
                item.iconColor ||
                "#CBD5E1"
            }
            className="shrink-0"
          />
        )}

        <span
          className={`max-w-full truncate text-[8px] min-[380px]:text-[9px] sm:text-[10px] md:text-[10px] leading-tight font-medium uppercase tracking-wide ${isActive
            ? "text-indigo-400"
            : "text-slate-300"
            }`}
        >
          {item.bottomLabel?.trim() ||
            item.label}
        </span>
      </a>
    );
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
          ? "bg-slate-900/20 backdrop-blur-lg shadow-lg"
          : "bg-slate-900/10 backdrop-blur-md"
          }`}
      >
        <div className="max-w-full mx-auto px-3 sm:px-10 lg:px-16 xl:px-20">
          <div className="flex items-center justify-between h-16 sm:h-[72px] md:h-20">
            <a
              href="/"
              className="flex-shrink-0 absolute left-1/2 -translate-x-1/2 lg:static lg:translate-x-0"
            >
              <img
                src={home?.headerLogo}
                alt="Vinayak V Thayil"
                className="h-12 min-[380px]:h-14 sm:h-16 md:h-18 lg:h-20 object-contain transition-all duration-300"
              />
            </a>

            <div className="hidden lg:flex items-center gap-6 flex-shrink-0 ml-auto">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/30 backdrop-blur-sm border border-slate-700/30">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />

                <span className="text-xs text-slate-300 font-medium whitespace-nowrap">
                  {dateTime.toLocaleDateString(
                    undefined,
                    {
                      weekday: "long",
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    }
                  )}
                  {" | "}
                  {dateTime.toLocaleTimeString(
                    undefined,
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: false,
                    }
                  )}
                </span>
              </div>

              <nav className="flex items-center gap-4 xl:gap-6">
                {visibleItems.map((item) => {
                  const target = getItemKey(item);

                  const hasDropdown =
                    item.hasDropdown &&
                    Array.isArray(item.children) &&
                    item.children.length > 0;

                  const hasActiveChild =
                    hasDropdown &&
                    item.children.some((child) =>
                      isChildActive(child)
                    );

                  const isActive =
                    activeSection === target ||
                    hasActiveChild;

                  const TopIcon = resolveIcon(
                    item.icon?.trim()
                  );

                  return (
                    <div
                      key={item.id}
                      className="relative"
                    >
                      <a
                        href={`#${target}`}
                        onClick={(event) => {
                          if (hasDropdown) {
                            event.preventDefault();
                            setIsOpen((prev) => !prev);
                          } else {
                            handleNavClick(event, item);
                          }
                        }}
                        className={`flex items-center gap-1 text-sm font-medium uppercase font-mono transition-all duration-300 ${isActive
                            ? "text-indigo-400 font-semibold"
                            : "text-slate-200 hover:text-indigo-400"
                          }`}
                      >
                        {TopIcon && (
                          <TopIcon
                            size={23}
                            strokeWidth={2}
                            color={
                              isActive
                                ? "#818CF8"
                                : item.iconColor || "currentColor"
                            }
                            className="shrink-0"
                          />
                        )}

                        <span>{item.label}</span>

                        {hasDropdown && (
                          <span
                            className={`inline-block text-[10px] ml-0.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
                              }`}
                          >
                            ▼
                          </span>
                        )}
                      </a>

                      {hasDropdown && (
                        <div
                          className={`absolute left-0 mt-2 w-max min-w-[150px] bg-slate-900 rounded-lg shadow-lg border-l-2 border-indigo-400/30 py-2 transition-all duration-200 z-50 ${isOpen
                              ? "opacity-100 visible"
                              : "opacity-0 invisible"
                            }`}
                        >
                          {item.children
                            .filter(
                              (child) =>
                                child.show !== false &&
                                child.isActive !== false
                            )
                            .map((child) => {
                              const childActive = isChildActive(child);

                              return (
                                <a
                                  key={child.id}
                                  href={`#${getItemKey(child)}`}
                                  onClick={(event) =>
                                    handleNavClick(event, child)
                                  }
                                  className={`flex items-center px-4 py-2 text-sm font-mono whitespace-nowrap uppercase transition-colors duration-200 ${childActive
                                      ? "text-indigo-400 bg-indigo-500/10"
                                      : "text-slate-300 hover:bg-slate-700/50 hover:text-indigo-400"
                                    }`}
                                >
                                  {child.label}
                                </a>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      </header>

      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-slate-900/10 backdrop-blur-xl border-t border-white/10 pb-[env(safe-area-inset-bottom)] h-[58px] min-[380px]:h-[60px] sm:h-[64px] md:h-[68px]">
        <div className="relative flex items-end justify-between h-full px-1 min-[380px]:px-2 sm:px-4 md:px-8 max-w-[700px] mx-auto">
          {(() => {
            const middleIndex = Math.ceil(
              normalBottomItems.length / 2
            );

            const leftItems =
              normalBottomItems.slice(
                0,
                middleIndex
              );

            const rightItems =
              normalBottomItems.slice(
                middleIndex
              );

            return (
              <>
                {leftItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex-1 min-w-0 h-full"
                  >
                    {renderBottomItem(item)}
                  </div>
                ))}

                {featuredBottomItem && (
                  <div className="flex-1 min-w-0 h-full flex flex-col items-center justify-end relative">
                    {(() => {
                      const FeaturedIcon =
                        getBottomIcon(
                          featuredBottomItem
                        );

                      const target =
                        getItemKey(
                          featuredBottomItem
                        );

                      const isActive =
                        activeSection ===
                        target ||
                        activeMenuChild ===
                        target;

                      return (
                        <>
                          <a
                            href={`#${target}`}
                            onClick={(event) =>
                              handleNavClick(
                                event,
                                featuredBottomItem
                              )
                            }
                            aria-label={
                              featuredBottomItem.bottomLabel?.trim() ||
                              featuredBottomItem.label
                            }
                            className="absolute z-30 flex items-center justify-center rounded-full bg-[#080808] border-[2px] border-amber-400 shadow-xl shadow-black/60 w-[50px] h-[50px] -top-[17px] min-[380px]:w-[52px] min-[380px]:h-[52px] min-[380px]:-top-[18px] sm:w-[56px] sm:h-[56px] sm:-top-[20px] md:w-[55px] md:h-[55px] md:-top-[21px] hover:scale-105 active:scale-95 transition-transform duration-200"
                          >
                            {FeaturedIcon && (
                              <FeaturedIcon
                                size={23}
                                strokeWidth={2}
                                color={
                                  featuredBottomItem.bottomIconColor ||
                                  featuredBottomItem.iconColor ||
                                  "#FFFFFF"
                                }
                              />
                            )}
                          </a>

                          <span
                            className={`absolute bottom-[4px] sm:bottom-[5px] md:bottom-[6px] text-[8px] min-[380px]:text-[9px] sm:text-[10px] md:text-[10px] font-medium uppercase tracking-wide whitespace-nowrap ${isActive
                              ? "text-indigo-400"
                              : "text-slate-300"
                              }`}
                          >
                            {featuredBottomItem.bottomLabel?.trim() ||
                              featuredBottomItem.label}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                )}

                {rightItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex-1 min-w-0 h-full"
                  >
                    {renderBottomItem(item)}
                  </div>
                ))}

                <div className="flex-1 min-w-0 h-full">
                  <button
                    type="button"
                    onClick={toggleMenu}
                    aria-label="Open menu"
                    className={`w-full h-full flex flex-col items-center justify-center gap-1 transition-colors ${isMenuOpen
                      ? "text-indigo-400"
                      : "text-slate-300 hover:text-indigo-400"
                      }`}
                  >
                    {icons.Menu && (
                      <icons.Menu
                        size={20}
                        strokeWidth={2}
                      />
                    )}

                    <span className="text-[8px] min-[380px]:text-[9px] sm:text-[10px] md:text-[10px] font-medium uppercase tracking-wide whitespace-nowrap">
                      More
                    </span>
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-opacity duration-300 ${isMenuOpen
          ? "opacity-100 visible"
          : "opacity-0 invisible pointer-events-none"
          }`}
      >
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={closeMenu}
        />

        <div
          className={`absolute inset-x-0 bottom-0 top-14 sm:top-16 bg-slate-900/95 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden transition-transform duration-300 ${isMenuOpen
            ? "translate-y-0"
            : "translate-y-6"
            }`}
        >
          <div className="relative flex flex-col items-center justify-center px-5 pt-4 pb-3 sm:pt-5 sm:pb-4 border-b border-white/10">
            <span className="text-indigo-400 text-base sm:text-lg font-semibold tracking-[0.2em] uppercase">
              More
            </span>

            <div className="mt-2 flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 bg-slate-800/30 backdrop-blur-sm border border-slate-700/30 whitespace-nowrap">
              <div className="w-2 h-2 shrink-0 bg-green-400 rounded-full animate-pulse" />

              <span className="text-xs text-slate-300 font-medium whitespace-nowrap tabular-nums">
                {dateTime.toLocaleDateString(
                  undefined,
                  {
                    weekday: "long",
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  }
                )}
                {" | "}
                {dateTime.toLocaleTimeString(
                  undefined,
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: false,
                  }
                )}
              </span>
            </div>

            <button
              type="button"
              onClick={closeMenu}
              className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-lg text-slate-300 hover:text-amber-400 transition-colors"
            >
              {icons.IoClose ? (
                <icons.IoClose size={20} />
              ) : (
                <span className="text-xl">
                  ×
                </span>
              )}
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-5 sm:px-6 py-3 sm:py-4">
            <div className="divide-y divide-white/10">
              {menuItems.map((item) => {
                const target = getItemKey(item);

                const hasChildren =
                  item.hasDropdown &&
                  Array.isArray(item.children) &&
                  item.children.length > 0;

                const isOpen =
                  mobileDropdownOpen ===
                  item.id;

                const childIsActive =
                  hasChildren &&
                  item.children.some((child) =>
                    isChildActive(child)
                  );

                const isActive =
                  activeSection === target ||
                  activeMenuChild === target ||
                  childIsActive;

                return (
                  <div key={item.id}>
                    <div className="flex items-center justify-between py-3 sm:py-4">
                      <a
                        href={`#${target}`}
                        onClick={(event) => {
                          if (hasChildren) {
                            handleMobileParentClick(
                              event,
                              item
                            );
                          } else {
                            handleNavClick(
                              event,
                              item
                            );
                          }
                        }}
                        className={`flex items-center flex-1 text-sm sm:text-base font-medium uppercase tracking-wide transition-colors ${isActive
                          ? "text-indigo-400"
                          : "text-slate-100 hover:text-indigo-400"
                          }`}
                      >
                        {item.label}
                      </a>

                      {hasChildren && (
                        <button
                          type="button"
                          onClick={() =>
                            toggleMobileDropdown(
                              item.id
                            )
                          }
                          className={`ml-2 p-2 rounded text-slate-300 transition-colors ${isActive
                            ? "text-indigo-400"
                            : ""
                            }`}
                          aria-label={`Toggle ${item.label} submenu`}
                        >
                          <span
                            className={`inline-block text-xs transition-transform ${isOpen
                              ? "rotate-180"
                              : ""
                              }`}
                          >
                            ▼
                          </span>
                        </button>
                      )}
                    </div>

                    {hasChildren && isOpen && (
                      <div className="pb-3 pl-5 sm:pl-6 space-y-3 border-l-2 border-indigo-400/30 ml-2 mb-2">
                        {item.children
                          .filter(
                            (child) =>
                              child.show !== false &&
                              child.isActive !== false
                          )
                          .map((child) => {
                            const childActive =
                              isChildActive(child);

                            return (
                              <a
                                key={child.id}
                                href={`#${getItemKey(
                                  child
                                )}`}
                                onClick={(event) => {
                                  setActiveMenuChild(
                                    getCurrentChildKey(
                                      child
                                    )
                                  );

                                  if (
                                    child.type ===
                                    "projects"
                                  ) {
                                    setActiveProjectFilter(
                                      child.filter
                                    );
                                  }

                                  handleNavClick(
                                    event,
                                    child
                                  );
                                }}
                                className={`block text-xs sm:text-sm uppercase tracking-wide transition-colors ${childActive
                                  ? "text-indigo-400 font-semibold"
                                  : "text-slate-100 hover:text-indigo-400"
                                  }`}
                              >
                                {child.label}
                              </a>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Navbar;